/**
 * One monotonic clock per provider attempt or client utterance. Compressed MP3
 * bytes reconstructed from Base64 are not PCM-decoded, playable or heard.
 * Only bounded durations/enum fields leave this collector; no content or IDs.
 */
export type SpeechDeliveryStage =
  | "request_start" | "response_headers" | "first_nonempty_transport_chunk"
  | "transport_eof" | "payload_complete" | "first_mp3_bytes_reconstructed" | "html_playing";
export type SpeechDeliveryTerminal = "complete" | "stop" | "error";
export type SpeechDeliveryReason =
  | "ended" | "provider_complete" | "stop" | "superseded" | "unmount"
  | "cancelled" | "request_failed" | "quota_rejected" | "playback_failed";
export interface SpeechDeliveryTimingReport {
  readonly schema_version: 1;
  readonly scope: "provider_clip" | "client_utterance";
  readonly cohort: "provider_sse" | "negotiated_request" | "legacy_json";
  readonly terminal: SpeechDeliveryTerminal;
  readonly terminal_reason: SpeechDeliveryReason;
  readonly request_to_response_headers_ms: number | null;
  readonly request_to_first_nonempty_transport_chunk_ms: number | null;
  readonly request_to_transport_eof_ms: number | null;
  readonly request_to_payload_complete_ms: number | null;
  readonly request_to_first_mp3_bytes_reconstructed_ms: number | null;
  readonly request_to_html_playing_ms: number | null;
  readonly first_mp3_bytes_reconstructed_to_html_playing_ms: number | null;
  readonly total_ms: number | null;
}
export interface SpeechDeliveryTiming {
  point(stage: SpeechDeliveryStage): void;
  finish(terminal: SpeechDeliveryTerminal, reason: SpeechDeliveryReason): void;
  discard(): void;
}
export function createSpeechDeliveryTiming(options: {
  scope: SpeechDeliveryTimingReport["scope"];
  cohort: SpeechDeliveryTimingReport["cohort"];
  isCurrent?: () => boolean;
  now?: () => number;
  onResult: (report: SpeechDeliveryTimingReport) => void;
}): SpeechDeliveryTiming {
  let closed = false, last: number | null = null;
  const points: Partial<Record<SpeechDeliveryStage, number | null>> = {};
  const seen = (stage: SpeechDeliveryStage) => Object.prototype.hasOwnProperty.call(points, stage);
  const current = () => {
    if (closed) return false;
    try { if (!options.isCurrent || options.isCurrent()) return true; } catch { /* Revocation. */ }
    closed = true;
    return false;
  };
  const clock = () => {
    try {
      const at = (options.now ?? (() => performance.now()))();
      if (!Number.isFinite(at) || at < 0 || last !== null && at < last) return null;
      last = at;
      return at;
    } catch { return null; }
  };
  const elapsed = (from: number | null | undefined, to: number | null | undefined) => {
    if (from == null || to == null) return null;
    const value = to - from;
    return Number.isFinite(value) && value >= 0 && value <= 600_000 ? value : null;
  };
  return {
    point(stage) {
      if (!current() || seen(stage) || stage !== "request_start" && !seen("request_start")) return;
      if ((stage === "first_nonempty_transport_chunk" || stage === "transport_eof") && !seen("response_headers")) return;
      if (stage === "html_playing" && (options.scope !== "client_utterance" || !seen("first_mp3_bytes_reconstructed"))) return;
      points[stage] = clock();
    },
    finish(terminal, reason) {
      if (!current() || !seen("request_start")) return;
      const at = clock(), start = points.request_start;
      const report: SpeechDeliveryTimingReport = Object.freeze({
        schema_version: 1, scope: options.scope, cohort: options.cohort,
        terminal, terminal_reason: reason,
        request_to_response_headers_ms: elapsed(start, points.response_headers),
        request_to_first_nonempty_transport_chunk_ms: elapsed(start, points.first_nonempty_transport_chunk),
        request_to_transport_eof_ms: elapsed(start, points.transport_eof),
        request_to_payload_complete_ms: elapsed(start, points.payload_complete),
        request_to_first_mp3_bytes_reconstructed_ms: elapsed(start, points.first_mp3_bytes_reconstructed),
        request_to_html_playing_ms: elapsed(start, points.html_playing),
        first_mp3_bytes_reconstructed_to_html_playing_ms: elapsed(points.first_mp3_bytes_reconstructed, points.html_playing),
        total_ms: elapsed(start, at),
      });
      closed = true;
      try { void Promise.resolve(options.onResult(report)).catch(() => {}); } catch { /* Optional diagnostics. */ }
    },
    discard() { closed = true; },
  };
}
